
import { supabase } from '@/integrations/supabase/client';
import { getCategoryByName, createCategory } from '../categoryHelpers';
import { getProductUploadResponseMessage } from './responseFormatters';

/**
 * Bulk create products with additional details like category and units
 * @param products Array of product objects with details
 * @returns Information about created, updated, and skipped products
 */
export const bulkCreateDetailedProducts = async (products: Array<{
  name: string;
  category?: string | null;
  description?: string | null;
  unit_of_measure?: string;
  product_id?: string;
}>) => {
  try {
    console.log('Processing products:', products);
    
    // First, get all categories to map names to IDs
    const { data: categories, error: categoryError } = await supabase
      .from('categories')
      .select('id, name');
    
    if (categoryError) throw categoryError;
    
    // Get existing products to check for duplicates and possibly update
    const { data: existingProducts, error: productsError } = await supabase
      .from('products')
      .select('id, name, unit_of_measure, description, category_id');
    
    if (productsError) throw productsError;
    
    const categoryMap = new Map(categories.map(cat => [cat.name.toLowerCase(), cat.id]));
    
    // Track products to insert and update separately
    const productsToInsert = [];
    const productsToUpdate = [];
    const existingProductsMap = new Map();
    
    console.log('Existing products:', existingProducts);
    console.log('Categories:', categories);
    console.log('Category map:', Array.from(categoryMap.entries()));
    
    // Create a map of existing products (lowercase name to full product object)
    existingProducts.forEach(product => {
      existingProductsMap.set(product.name.toLowerCase(), product);
    });

    // Process each product - either create new or update if attributes differ
    for (const product of products) {
      const categoryId = await processCategoryForProduct(product, categoryMap);      
      await processProductForBulkOperation(product, existingProductsMap, categoryId, productsToInsert, productsToUpdate);
    }
    
    console.log('Products to insert:', productsToInsert.length);
    console.log('Products to update:', productsToUpdate.length);
    
    const { insertedData, addedCount } = await insertNewProducts(productsToInsert);
    const { updatedData, updatedCount } = await updateExistingProducts(productsToUpdate);
    
    // Combine results for return
    const resultData = [
      ...(insertedData || []),
      ...(updatedData || [])
    ];
    
    console.log('Final results:', {
      added: addedCount,
      updated: updatedCount,
      skipped: products.length - addedCount - updatedCount,
    });
    
    return { 
      data: resultData,
      added: addedCount,
      updated: updatedCount,
      skipped: products.length - addedCount - updatedCount,
      message: getProductUploadResponseMessage(addedCount, updatedCount, products.length)
    };
  } catch (error) {
    console.error("Failed to upload products:", error);
    throw error;
  }
};

/**
 * Process category for a product - either find existing one or create new one
 * @param product Product with optional category
 * @param categoryMap Map of existing category names to IDs
 * @returns Category ID or null if no category
 */
async function processCategoryForProduct(
  product: { category?: string | null },
  categoryMap: Map<string, string>
): Promise<string | null> {
  // Handle category - ensure it's not 'none' and normalize by trimming and lowercasing
  if (!product.category || product.category.trim() === '' || product.category.toLowerCase() === 'none') {
    console.log('No valid category specified for product');
    return null;
  }
  
  const normalizedCategoryName = product.category.trim();
  console.log('Looking for category:', normalizedCategoryName);
  
  // Check if category exists using case-insensitive lookup
  let categoryId = categoryMap.get(normalizedCategoryName.toLowerCase());
  console.log('Category ID from map:', categoryId);
  
  // If category doesn't exist, create it
  if (!categoryId) {
    console.log('Category not found, creating new category:', normalizedCategoryName);
    try {
      const newCategory = await createCategory(normalizedCategoryName);
      if (newCategory) {
        categoryId = newCategory.id;
        // Add to our map for future lookups
        categoryMap.set(normalizedCategoryName.toLowerCase(), newCategory.id);
        console.log('Created new category with ID:', categoryId);
      }
    } catch (err) {
      console.error(`Error creating category ${normalizedCategoryName}:`, err);
    }
  }

  return categoryId;
}

/**
 * Process a product for bulk operation - determine if it needs to be inserted or updated
 * @param product Product to process
 * @param existingProductsMap Map of existing products
 * @param categoryId Category ID for the product
 * @param productsToInsert Array to collect products that need to be inserted
 * @param productsToUpdate Array to collect products that need to be updated
 */
async function processProductForBulkOperation(
  product: {
    name: string;
    description?: string | null;
    unit_of_measure?: string;
  },
  existingProductsMap: Map<string, any>,
  categoryId: string | null,
  productsToInsert: any[],
  productsToUpdate: any[]
): Promise<void> {
  console.log('Processing product:', product.name, 'with category ID:', categoryId);
  
  const existingProduct = existingProductsMap.get(product.name.toLowerCase());
  console.log('Existing product found:', existingProduct);
  
  // If product exists but has different unit of measure or other data, update it
  if (existingProduct) {
    const needsUpdate = (
      (product.unit_of_measure && existingProduct.unit_of_measure !== product.unit_of_measure) ||
      (product.description && existingProduct.description !== product.description) ||
      (categoryId && existingProduct.category_id !== categoryId)
    );
    
    if (needsUpdate) {
      console.log('Product needs update:', product.name, 'Category ID:', categoryId);
      productsToUpdate.push({
        id: existingProduct.id,
        unit_of_measure: product.unit_of_measure || existingProduct.unit_of_measure,
        description: product.description || existingProduct.description,
        category_id: categoryId || existingProduct.category_id
      });
    } else {
      console.log('Product exists with same attributes, skipping:', product.name);
    }
    return;
  }
  
  // Add to insert list for new products
  console.log('Adding new product:', product.name, 'with category ID:', categoryId);
  productsToInsert.push({
    name: product.name,
    category_id: categoryId,
    description: product.description || null,
    unit_of_measure: product.unit_of_measure || 'kg'
  });
}

/**
 * Insert new products into the database
 * @param productsToInsert Array of products to insert
 * @returns Inserted data and count of added products
 */
async function insertNewProducts(productsToInsert: any[]) {
  let insertedData = null;
  let addedCount = 0;
  
  if (productsToInsert.length > 0) {
    const { data, error } = await supabase
      .from('products')
      .insert(productsToInsert)
      .select();
    
    if (error) {
      console.error('Error inserting products:', error);
      throw error;
    }
    insertedData = data;
    addedCount = productsToInsert.length;
    console.log('Products inserted successfully:', addedCount);
  }
  
  return { insertedData, addedCount };
}

/**
 * Update existing products in the database
 * @param productsToUpdate Array of products to update
 * @returns Updated data and count of updated products
 */
async function updateExistingProducts(productsToUpdate: any[]) {
  let updatedData: any[] = [];
  let updatedCount = 0;
  
  if (productsToUpdate.length > 0) {
    for (const product of productsToUpdate) {
      console.log('Updating product:', product.id, 'with category_id:', product.category_id);
      const { data, error } = await supabase
        .from('products')
        .update({ 
          unit_of_measure: product.unit_of_measure,
          description: product.description,
          category_id: product.category_id
        })
        .eq('id', product.id)
        .select();
      
      if (error) {
        console.error("Error updating product:", error);
      } else if (data) {
        updatedCount++;
        updatedData = [...updatedData, ...data];
        console.log('Product updated successfully:', data);
      }
    }
  }
  
  return { updatedData, updatedCount };
}
